import { Icon } from '@iconify/react';
import { printConfigFor } from './invoiceSettingDefaults';
import { useNavigate } from 'react-router-dom';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_RECEIPTS_CREATE } from '../../../../../Routing/routes';

const InvoiceDetailModal = ({ invoice, loading, onClose, onVoid, onPost, voidLoading, postLoading }) => {
    const navigate = useNavigate();
    if (!invoice) return null;

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'PAID': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'POSTED': return 'tw-bg-blue-100 tw-text-blue-700';
            case 'DRAFT': return 'tw-bg-gray-100 tw-text-gray-700';
            case 'PARTIAL': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'VOID': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    const getPaymentModeIcon = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'cash': return 'mdi:cash';
            case 'cheque': return 'mdi:checkbook';
            case 'upi': return 'mdi:cellphone-nfc';
            case 'net banking':
            case 'neft':
            case 'rtgs': return 'mdi:bank-transfer';
            case 'card': return 'mdi:credit-card-outline';
            case 'wallet': return 'mdi:wallet-outline';
            default: return 'mdi:cash';
        }
    };

    const getPaymentModeColor = (mode) => {
        switch (mode?.toLowerCase()) {
            case 'cash': return { bg: 'tw-bg-emerald-500', light: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-200' };
            case 'cheque': return { bg: 'tw-bg-blue-500', light: 'tw-bg-blue-50', text: 'tw-text-blue-600', border: 'tw-border-blue-200' };
            case 'upi': return { bg: 'tw-bg-purple-500', light: 'tw-bg-purple-50', text: 'tw-text-purple-600', border: 'tw-border-purple-200' };
            case 'net banking':
            case 'neft':
            case 'rtgs': return { bg: 'tw-bg-indigo-500', light: 'tw-bg-indigo-50', text: 'tw-text-indigo-600', border: 'tw-border-indigo-200' };
            case 'card': return { bg: 'tw-bg-amber-500', light: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-200' };
            case 'wallet': return { bg: 'tw-bg-pink-500', light: 'tw-bg-pink-50', text: 'tw-text-pink-600', border: 'tw-border-pink-200' };
            default: return { bg: 'tw-bg-gray-500', light: 'tw-bg-gray-50', text: 'tw-text-gray-600', border: 'tw-border-gray-200' };
        }
    };

    // Config snapshot
    const config = printConfigFor(invoice);

    // Map API response fields
    const invoiceNo = invoice.document_number || '-';
    const member = invoice.member_details?.name || '-';
    const flat = invoice.flat || {};
    const unit = flat.flat_number || invoice.member_details?.flat_no || '-';
    const building = flat.wing?.title || '-';

    // Entity details from ownership
    const ownership = flat.ownerships?.[0] || {};
    const entity = ownership.entity || {};
    const phone = entity.phone || invoice.member_details?.phone || '-';
    const email = entity.email || invoice.member_details?.email || '-';
    const memberGst = entity.gstin_no || invoice.member_details?.gst_no || '';
    const memberPan = entity.pan_no || '';

    const invoiceType = invoice.invoiceType?.invoice_title || 'Invoice';
    const status = invoice.status || 'DRAFT';
    const invoiceDate = invoice.bill_date || invoice.invoice_date;
    const dueDate = invoice.due_date;
    const netPayable = parseFloat(invoice.net_payable || 0);
    const amountPaid = parseFloat(invoice.amount_paid || 0);
    const balance = parseFloat(invoice.balance_due || 0) || (netPayable - amountPaid);

    // Amounts
    const taxAmount = parseFloat(invoice.tax_amount || 0);
    const arrearsAmount = parseFloat(invoice.arrears_amount || 0);
    const interestAmount = parseFloat(invoice.interest_amount || 0);
    const roundOffAmount = parseFloat(invoice.round_off_amount || 0);
    const adjustmentAmount = parseFloat(invoice.adjustment_amount || 0);
    const discountAmount = parseFloat(invoice.discount_amount || 0);

    // Line items from API
    const allItems = invoice.items || [];

    // Separate charge items from system items
    const SYSTEM_HEAD_CODES = ['CGST', 'SGST', 'IGST', 'PAR', 'DNR', 'INT', 'RND', 'ADJ'];
    const chargeItems = allItems.filter(item => !SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));
    const systemItems = allItems.filter(item => SYSTEM_HEAD_CODES.includes(item.tariffHead?.head_code));

    const arrearCodes = ['PAR', 'DNR', 'INT'];
    const taxCodes = ['CGST', 'SGST', 'IGST'];
    const arrearItems = systemItems.filter(item => arrearCodes.includes(item.tariffHead?.head_code));
    const hasItemWithAmount = (code) => arrearItems.some(
        item => item.tariffHead?.head_code === code
            && parseFloat(item.base_amount || item.system_amount || 0) !== 0
    );
    const hasArrearItem = hasItemWithAmount('PAR') || hasItemWithAmount('DNR');
    const hasInterestItem = hasItemWithAmount('INT');
    const taxItems = systemItems.filter(item => taxCodes.includes(item.tariffHead?.head_code));
    const otherItems = systemItems.filter(item => ['RND', 'ADJ'].includes(item.tariffHead?.head_code));

    const chargeSubtotal = chargeItems.reduce((sum, item) => sum + parseFloat(item.base_amount || item.system_amount || 0), 0);

    // A draft is not billed yet, and a carried forward invoice is collected on its successor -
    // neither can take a payment of its own
    const isCarriedForward = invoice.is_carried_forward === 1;
    const canCollectPayment = !['DRAFT', 'PAID', 'VOID', 'CANCELLED'].includes(status) && !isCarriedForward;
    const canSendReminder = status !== 'DRAFT';
    const showPaymentReceipts = status !== 'DRAFT';

    // Receipt allocations from API
    const receiptAllocations = invoice.receiptInvoiceAllocations || [];
    const totalReceived = receiptAllocations.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);

    const COMPONENT_LABELS = {
        PRINCIPAL: 'Principal',
        INTEREST: 'Interest',
        ARREAR: 'Arrears',
        DEBIT_NOTE_ARREAR: 'DN Arrears',
    };

    const getReceiptStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CLEARED': return 'tw-bg-emerald-100 tw-text-emerald-700';
            case 'PENDING': return 'tw-bg-amber-100 tw-text-amber-700';
            case 'BOUNCED': return 'tw-bg-red-100 tw-text-red-700';
            case 'CANCELLED': return 'tw-bg-gray-200 tw-text-gray-600';
            default: return 'tw-bg-gray-100 tw-text-gray-700';
        }
    };

    // Generate insights
    const getInvoiceInsights = () => {
        const insights = [];

        if (status === 'VOID' || status === 'CANCELLED') {
            insights.push({ type: 'warning', icon: 'mdi:alert-circle', text: 'This invoice has been voided/cancelled.' });
        }

        if (status === 'PARTIAL') {
            const percentPaid = Math.round((amountPaid / netPayable) * 100);
            insights.push({ type: 'info', icon: 'mdi:chart-pie', text: `${percentPaid}% paid. Balance of ${formatCurrency(balance)} remaining.` });
        }

        if (status === 'POSTED' && dueDate) {
            const dueDateObj = new Date(dueDate);
            const today = new Date();
            if (dueDateObj < today) {
                const daysOverdue = Math.floor((today - dueDateObj) / (1000 * 60 * 60 * 24));
                insights.push({ type: 'warning', icon: 'mdi:alert-circle', text: `Payment is ${daysOverdue} days overdue. Consider sending a reminder.` });
            }
        }

        if (receiptAllocations.length > 1) {
            const modes = [...new Set(receiptAllocations.map(a => a.receipt?.payment_mode).filter(Boolean))];
            if (modes.length > 1) {
                insights.push({ type: 'success', icon: 'mdi:check-decagram', text: `Paid via ${modes.length} different payment methods.` });
            }
        }

        if (status === 'DRAFT') {
            insights.push({ type: 'tip', icon: 'mdi:lightbulb-outline', text: `This is a draft invoice. Post it to make it official.` });
        }

        if (status === 'PAID') {
            insights.push({ type: 'success', icon: 'mdi:check-circle', text: `Fully paid. Great payment history!` });
        }

        return insights;
    };

    const insights = getInvoiceInsights();

    if (loading) {
        return (
            <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-p-8 tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4">Loading invoice details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
                {/* Modal Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:file-document-outline" className="tw-text-white tw-text-xl" />
                        </div>
                        <div>
                            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">{invoiceNo}</h2>
                            <p className="tw-text-xs tw-text-gray-500">{invoiceType}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${getStatusColor(status)}`}>
                            {status}
                        </span>
                        {invoice.is_carried_forward === 1 && (
                            <span className="tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-medium tw-bg-orange-100 tw-text-orange-700">
                                C/F
                            </span>
                        )}
                        <button onClick={onClose} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-400">
                            <Icon icon="mdi:close" className="tw-text-xl" />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
                    <div className="tw-grid tw-grid-cols-3 tw-gap-5">
                        {/* Left Column - Invoice Details */}
                        <div className="tw-col-span-2 tw-space-y-4">
                            {/* Member Info */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                    <Icon icon="mdi:account-outline" className="tw-text-gray-400" /> Bill To
                                </h3>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                    <div>
                                        <p className="tw-font-medium tw-text-gray-800">{member}</p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-mt-1">{building}-{unit} {flat.flat_type ? `| ${flat.flat_type}` : ''}</p>
                                        {memberGst && (
                                            <p className="tw-text-xs tw-text-blue-600 tw-mt-1 tw-font-medium">GSTIN: {memberGst}</p>
                                        )}
                                        {memberPan && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">PAN: {memberPan}</p>
                                        )}
                                    </div>
                                    <div className="tw-text-right">
                                        <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1">
                                            <Icon icon="mdi:phone-outline" /> {phone}
                                        </p>
                                        <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1">
                                            <Icon icon="mdi:email-outline" /> {email}
                                        </p>
                                        {config.show_flat_area === 1 && (flat.carpet_area || invoice.member_details?.flat_area) && (
                                            <p className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-justify-end tw-gap-1 tw-mt-1">
                                                <Icon icon="mdi:ruler-square" /> {config.area_label || 'Area'}: {flat.carpet_area || invoice.member_details?.flat_area} sq.ft
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Charge Items */}
                            <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                <div className="tw-px-4 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:format-list-bulleted" className="tw-text-gray-400" /> Invoice Items
                                    </h3>
                                </div>
                                <table className="tw-w-full tw-text-sm">
                                    <thead>
                                        <tr className="tw-border-b tw-border-gray-100">
                                            <th className="tw-text-left tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Description</th>
                                            {config.show_detailed_gst === 1 && (
                                                <th className="tw-text-right tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Tax</th>
                                            )}
                                            <th className="tw-text-right tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-500">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {chargeItems.map((item, idx) => {
                                            const itemAmount = parseFloat(item.base_amount || item.system_amount || 0);
                                            return (
                                                <tr key={idx} className="tw-border-b tw-border-gray-50">
                                                    <td className="tw-px-4 tw-py-2 tw-text-gray-700">
                                                        {item.tariffHead?.head_name || item.description || '-'}
                                                        {item.description && item.tariffHead?.head_name && item.description !== item.tariffHead.head_name && (
                                                            <span className="tw-text-xs tw-text-gray-400 tw-ml-1">({item.description})</span>
                                                        )}
                                                        {config.show_detailed_gst === 1 && item.taxRule && (
                                                            <p className="tw-text-[10px] tw-text-indigo-500 tw-mt-0.5">{item.taxRule.rule_name}</p>
                                                        )}
                                                    </td>
                                                    {config.show_detailed_gst === 1 && (
                                                        <td className="tw-px-4 tw-py-2 tw-text-right tw-text-gray-500">
                                                            {formatCurrency(item.tax_amount)}
                                                        </td>
                                                    )}
                                                    <td className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">
                                                        {formatCurrency(itemAmount)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {chargeItems.length === 0 && (
                                            <tr>
                                                <td colSpan={config.show_detailed_gst === 1 ? 3 : 2} className="tw-px-4 tw-py-4 tw-text-center tw-text-gray-400">
                                                    No items found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        {/* Subtotal */}
                                        <tr className="tw-border-t tw-border-gray-100">
                                            <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-2 tw-text-right tw-text-gray-600 tw-text-xs">Subtotal</td>
                                            <td className="tw-px-4 tw-py-2 tw-text-right tw-font-medium tw-text-gray-800">{formatCurrency(chargeSubtotal)}</td>
                                        </tr>

                                        {/* Tax items */}
                                        {taxItems.map((item, idx) => {
                                            const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                            if (amount === 0) return null;
                                            return (
                                                <tr key={`tax-${idx}`} className="tw-border-t tw-border-gray-50">
                                                    <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">{item.tariffHead?.head_name || item.description}</td>
                                                    <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">{formatCurrency(amount)}</td>
                                                </tr>
                                            );
                                        })}

                                        {/* Total tax if no detailed items */}
                                        {taxItems.length === 0 && taxAmount > 0 && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">Tax</td>
                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-700">{formatCurrency(taxAmount)}</td>
                                            </tr>
                                        )}

                                        {/* Arrear items */}
                                        {arrearItems.map((item, idx) => {
                                            const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                            if (amount === 0) return null;
                                            return (
                                                <tr key={`arrear-${idx}`} className="tw-border-t tw-border-gray-50">
                                                    <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-amber-700 tw-text-xs tw-font-medium">{item.tariffHead?.head_name || item.description}</td>
                                                    <td className={`tw-px-4 tw-py-1.5 tw-text-right tw-font-medium ${amount < 0 ? 'tw-text-red-600' : 'tw-text-amber-700'}`}>
                                                        {amount < 0 ? '- ' : ''}{formatCurrency(Math.abs(amount))}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* Arrears / interest from invoice level when there is no
                                            line item for that component. Both rows used to be gated on
                                            arrearItems.length === 0, so an invoice carrying a PAR item
                                            but no INT item suppressed the Interest row entirely and the
                                            interest never appeared. Each falls back on its own codes. */}
                                        {!hasArrearItem && arrearsAmount > 0 && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-amber-700 tw-text-xs tw-font-medium">Arrears</td>
                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-font-medium tw-text-amber-700">{formatCurrency(arrearsAmount)}</td>
                                            </tr>
                                        )}
                                        {!hasInterestItem && interestAmount > 0 && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-amber-700 tw-text-xs tw-font-medium">Interest</td>
                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-font-medium tw-text-amber-700">{formatCurrency(interestAmount)}</td>
                                            </tr>
                                        )}

                                        {/* Discount */}
                                        {discountAmount > 0 && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-green-600 tw-text-xs">Discount</td>
                                                <td className="tw-px-4 tw-py-1.5 tw-text-right tw-text-green-600">-{formatCurrency(discountAmount)}</td>
                                            </tr>
                                        )}

                                        {/* Other system items (Round Off, Adjustment) */}
                                        {otherItems.map((item, idx) => {
                                            const amount = parseFloat(item.base_amount || item.system_amount || 0);
                                            if (amount === 0) return null;
                                            return (
                                                <tr key={`other-${idx}`} className="tw-border-t tw-border-gray-50">
                                                    <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">{item.tariffHead?.head_name || item.description}</td>
                                                    <td className={`tw-px-4 tw-py-1.5 tw-text-right ${amount < 0 ? 'tw-text-red-600' : 'tw-text-gray-700'}`}>
                                                        {amount < 0 ? '- ' : ''}{formatCurrency(Math.abs(amount))}
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* Invoice-level round off/adjustment if not in items */}
                                        {roundOffAmount !== 0 && !otherItems.some(i => i.tariffHead?.head_code === 'RND') && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">Round Off</td>
                                                <td className={`tw-px-4 tw-py-1.5 tw-text-right ${roundOffAmount < 0 ? 'tw-text-red-600' : 'tw-text-gray-700'}`}>
                                                    {roundOffAmount < 0 ? '- ' : ''}{formatCurrency(Math.abs(roundOffAmount))}
                                                </td>
                                            </tr>
                                        )}
                                        {adjustmentAmount !== 0 && !otherItems.some(i => i.tariffHead?.head_code === 'ADJ') && (
                                            <tr className="tw-border-t tw-border-gray-50">
                                                <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-1.5 tw-text-right tw-text-gray-500 tw-text-xs">Adjustment</td>
                                                <td className={`tw-px-4 tw-py-1.5 tw-text-right ${adjustmentAmount < 0 ? 'tw-text-red-600' : 'tw-text-gray-700'}`}>
                                                    {adjustmentAmount < 0 ? '- ' : ''}{formatCurrency(Math.abs(adjustmentAmount))}
                                                </td>
                                            </tr>
                                        )}

                                        {/* Net Payable */}
                                        <tr className="tw-bg-gray-50">
                                            <td colSpan={config.show_detailed_gst === 1 ? 2 : 1} className="tw-px-4 tw-py-2 tw-font-semibold tw-text-gray-800 tw-text-right">Net Payable</td>
                                            <td className="tw-px-4 tw-py-2 tw-text-right tw-font-bold tw-text-gray-800">{formatCurrency(netPayable)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Payment Receipts - a draft cannot be paid against, so the whole block is hidden */}
                            {showPaymentReceipts && (
                            <div className="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                <div className="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-gray-50 tw-to-gray-100 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                                    <div className="tw-flex tw-items-center tw-gap-3">
                                        <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-emerald-500 tw-flex tw-items-center tw-justify-center">
                                            <Icon icon="mdi:receipt-text-outline" className="tw-text-white" />
                                        </div>
                                        <div>
                                            <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700">Payment Receipts</h3>
                                            <p className="tw-text-[10px] tw-text-gray-500">{receiptAllocations.length} payment(s) received</p>
                                        </div>
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        {receiptAllocations.length > 0 && (
                                            <span className="tw-text-xs tw-font-medium tw-text-emerald-600 tw-bg-emerald-50 tw-px-2 tw-py-1 tw-rounded-full">
                                                Total: {formatCurrency(totalReceived)}
                                            </span>
                                        )}
                                        {canCollectPayment && (
                                            <button
                                                onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_RECEIPTS_CREATE}?flat_id=${invoice.flat_id}&wing_id=${invoice.flat?.wing_id || ''}&invoice_id=${invoice.invoice_id}`)}
                                                className="tw-text-xs tw-text-white tw-bg-blue-600 tw-font-medium tw-flex tw-items-center tw-gap-1 hover:tw-bg-blue-700 tw-px-3 tw-py-1.5 tw-rounded-lg tw-transition-colors"
                                            >
                                                <Icon icon="mdi:plus" /> Add Payment
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {receiptAllocations.length > 0 ? (
                                    <div className="tw-p-3">
                                        <div className="tw-space-y-3">
                                            {receiptAllocations.map((allocation, index) => {
                                                const receipt = allocation.receipt || {};
                                                const components = allocation.components || [];
                                                const mode = receipt.payment_mode || 'Cash';
                                                const modeColors = getPaymentModeColor(mode);
                                                return (
                                                    <div
                                                        key={allocation.invoice_allocation_id || index}
                                                        className={`tw-rounded-xl tw-border ${modeColors.border} ${modeColors.light} tw-p-4 tw-transition-all hover:tw-shadow-md`}
                                                    >
                                                        <div className="tw-flex tw-items-start tw-justify-between">
                                                            <div className="tw-flex tw-items-start tw-gap-3">
                                                                <div className={`tw-w-11 tw-h-11 tw-rounded-xl ${modeColors.bg} tw-flex tw-items-center tw-justify-center tw-shadow-sm`}>
                                                                    <Icon icon={getPaymentModeIcon(mode)} className="tw-text-white tw-text-xl" />
                                                                </div>
                                                                <div>
                                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                                        <p className="tw-font-semibold tw-text-gray-800">
                                                                            {receipt.receipt_number || `RCP-${index + 1}`}
                                                                        </p>
                                                                        <span className={`tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium ${modeColors.light} ${modeColors.text} tw-border ${modeColors.border}`}>
                                                                            {mode}
                                                                        </span>
                                                                    </div>
                                                                    <div className="tw-flex tw-items-center tw-gap-3 tw-mt-1.5">
                                                                        <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                                                            <Icon icon="mdi:calendar-outline" className="tw-text-gray-400" />
                                                                            {formatDate(receipt.payment_date)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="tw-text-right">
                                                                <p className="tw-text-lg tw-font-bold tw-text-emerald-600">{formatCurrency(allocation.amount)}</p>
                                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium ${getReceiptStatusColor(receipt.status)}`}>
                                                                    <Icon icon="mdi:check-circle" className="tw-text-xs" />
                                                                    {receipt.status || 'Completed'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Component Breakdown */}
                                                        {components.length > 0 && (
                                                            <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200/50">
                                                                <p className="tw-text-[10px] tw-text-gray-400 tw-font-medium tw-mb-1.5 tw-uppercase tw-tracking-wider">Allocation Breakdown</p>
                                                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                                                    {components.map((comp, cIdx) => (
                                                                        <span key={cIdx} className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-px-2.5 tw-py-1">
                                                                            <span className="tw-text-gray-500">{COMPONENT_LABELS[comp.component] || comp.component}:</span>
                                                                            <span className="tw-font-medium tw-text-gray-800">{formatCurrency(comp.amount)}</span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {receipt.reference_no && (
                                                            <div className={`${components.length > 0 ? 'tw-mt-2' : 'tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200/50'} tw-flex tw-items-center tw-justify-between`}>
                                                                <span className="tw-text-xs tw-text-gray-500">
                                                                    <span className="tw-text-gray-400">Ref:</span> {receipt.reference_no}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="tw-px-4 tw-py-10 tw-text-center">
                                        <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
                                            <Icon icon="mdi:receipt-text-remove-outline" className="tw-text-3xl tw-text-gray-300" />
                                        </div>
                                        <p className="tw-text-gray-500 tw-font-medium">No payments received yet</p>
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-1">Payments will appear here once recorded</p>
                                        {canCollectPayment && (
                                            <button
                                                onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_RECEIPTS_CREATE}?flat_id=${invoice.flat_id}&wing_id=${invoice.flat?.wing_id || ''}&invoice_id=${invoice.invoice_id}`)}
                                                className="tw-mt-4 tw-text-sm tw-text-white tw-bg-blue-600 tw-font-medium tw-flex tw-items-center tw-gap-1.5 hover:tw-bg-blue-700 tw-px-4 tw-py-2 tw-rounded-lg tw-transition-colors tw-mx-auto"
                                            >
                                                <Icon icon="mdi:plus" /> Record First Payment
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            )}
                        </div>

                        {/* Right Column - Summary & Insights */}
                        <div className="tw-space-y-4">
                            {/* Amount Summary */}
                            <div className="tw-bg-gradient-to-br tw-from-blue-500 tw-to-blue-600 tw-rounded-lg tw-p-4 tw-text-white">
                                <p className="tw-text-blue-100 tw-text-xs tw-mb-1">Net Payable</p>
                                <p className="tw-text-2xl tw-font-bold">{formatCurrency(netPayable)}</p>
                                <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-blue-400/30 tw-space-y-2">
                                    <div className="tw-flex tw-justify-between tw-text-sm">
                                        <span className="tw-text-blue-100">Paid</span>
                                        <span className="tw-font-medium">{formatCurrency(amountPaid)}</span>
                                    </div>
                                    <div className="tw-flex tw-justify-between tw-text-sm">
                                        <span className="tw-text-blue-100">Balance</span>
                                        <span className="tw-font-medium">{formatCurrency(balance)}</span>
                                    </div>
                                </div>
                                {amountPaid > 0 && (
                                    <div className="tw-mt-3">
                                        <div className="tw-flex tw-justify-between tw-text-xs tw-mb-1">
                                            <span className="tw-text-blue-100">Progress</span>
                                            <span>{Math.round((amountPaid / netPayable) * 100)}%</span>
                                        </div>
                                        <div className="tw-w-full tw-bg-blue-400/30 tw-rounded-full tw-h-2">
                                            <div
                                                className="tw-bg-white tw-h-2 tw-rounded-full tw-transition-all"
                                                style={{ width: `${Math.min((amountPaid / netPayable) * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Dates */}
                            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-3">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:calendar-plus-outline" /> Bill Date
                                    </span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{formatDate(invoiceDate)}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                        <Icon icon="mdi:calendar-alert-outline" /> Due Date
                                    </span>
                                    <span className="tw-text-sm tw-font-medium tw-text-gray-800">{formatDate(dueDate)}</span>
                                </div>
                                {invoice.period_start && (
                                    <div className="tw-flex tw-items-center tw-justify-between">
                                        <span className="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
                                            <Icon icon="mdi:calendar-range" /> Period
                                        </span>
                                        <span className="tw-text-xs tw-font-medium tw-text-gray-800">
                                            {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Insights */}
                            {insights.length > 0 && (
                                <div className="tw-bg-gradient-to-br tw-from-purple-50 tw-to-indigo-50 tw-rounded-lg tw-p-4 tw-border tw-border-purple-100">
                                    <h3 className="tw-text-sm tw-font-semibold tw-text-purple-800 tw-mb-3 tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:robot-outline" className="tw-text-purple-500" /> Insights
                                    </h3>
                                    <div className="tw-space-y-2">
                                        {insights.map((insight, idx) => (
                                            <div key={idx} className={`tw-flex tw-items-start tw-gap-2 tw-text-xs tw-p-2 tw-rounded-md ${
                                                insight.type === 'warning' ? 'tw-bg-amber-50 tw-text-amber-800' :
                                                insight.type === 'success' ? 'tw-bg-emerald-50 tw-text-emerald-800' :
                                                insight.type === 'tip' ? 'tw-bg-blue-50 tw-text-blue-800' :
                                                'tw-bg-gray-50 tw-text-gray-700'
                                            }`}>
                                                <Icon icon={insight.icon} className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                                                <span>{insight.text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quick Actions */}
                            <div className="tw-space-y-2">
                                {status === 'DRAFT' && (
                                    <button
                                        onClick={onPost}
                                        disabled={postLoading}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-emerald-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-emerald-700 tw-transition-colors disabled:tw-opacity-50"
                                    >
                                        {postLoading ? (
                                            <>
                                                <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-white"></div>
                                                Posting...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="mdi:check-circle" /> Post Invoice
                                            </>
                                        )}
                                    </button>
                                )}
                                {canCollectPayment && (
                                    <button
                                        onClick={() => navigate(`${SOCIETY_PORTAL}${SOCIETY_PORTAL_RECEIPTS_CREATE}?flat_id=${invoice.flat_id}&wing_id=${invoice.flat?.wing_id || ''}&invoice_id=${invoice.invoice_id}`)}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 tw-transition-colors"
                                    >
                                        <Icon icon="mdi:cash-plus" /> Record Payment
                                    </button>
                                )}
                                {canSendReminder && (
                                    <button className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-gray-200 tw-text-gray-700 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-50 tw-transition-colors">
                                        <Icon icon="mdi:send-outline" /> Send Reminder
                                    </button>
                                )}
                                <button
                                    onClick={() => navigate(`${SOCIETY_PORTAL}/finance/invoices/${invoice.invoice_id}/print`)}
                                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-gray-200 tw-text-gray-700 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-50 tw-transition-colors"
                                >
                                    <Icon icon="mdi:printer" /> Print Invoice
                                </button>
                                {status === 'POSTED' && (
                                    <button
                                        onClick={onVoid}
                                        disabled={voidLoading}
                                        className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-border tw-border-red-200 tw-text-red-600 tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-red-50 tw-transition-colors disabled:tw-opacity-50"
                                    >
                                        {voidLoading ? (
                                            <>
                                                <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-b-2 tw-border-red-600"></div>
                                                Voiding...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="mdi:close-circle" /> Void Invoice
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetailModal;
